// The read model of the simulation: plain figures per tile, port, machine, belt, route and order,
// as the interface shows them. Nothing here changes the world.
import type { Speed } from './clock';
import type { ItemId, PortDef, PortKind, TileKind } from './data';
import { EPSILON, TICK_MS, type FlowStatus, type NodeFigures } from './flow';
import { opposite, type Cell, type Rotation, type Side, type Socket } from './geometry';
import type { FlowNode } from './network';
import type { Simulation } from './simulation';
import { machineCells, machineInput, machineOutput, roundTripMinutes, routeThroughput, storeTotal, type Store } from './world';

/** A piece of the chain that reaches no exit, so nothing flows through it. */
export type PieceStatus = FlowStatus | 'disconnected';

export interface ItemAmount {
  readonly item: ItemId;
  readonly name: string;
  readonly color: string;
  /** Items, or items per minute for rates. */
  readonly amount: number;
}

export interface PortReport {
  readonly id: string;
  readonly kind: PortKind;
  readonly side: Side;
  /** The interior cell the door opens into, for tiles with an interior. */
  readonly edge?: Socket;
  readonly stock: readonly ItemAmount[];
  readonly total: number;
  /** Null for the colony warehouse, which has no limit. */
  readonly capacity: number | null;
  readonly full: boolean;
  /** Items per minute arriving at and leaving the store, in total and by item. */
  readonly inRate: number;
  readonly outRate: number;
  readonly arriving: readonly ItemAmount[];
  readonly leaving: readonly ItemAmount[];
}

export interface MachineReport {
  readonly id: string;
  readonly type: string;
  readonly name: string;
  readonly recipe: string;
  readonly origin: Cell;
  readonly rotation: Rotation;
  readonly cells: readonly Cell[];
  readonly input: Socket;
  readonly output: Socket;
  readonly crafts: number;
  readonly maxCrafts: number;
  /** Share of its full speed, from 0 to 1. */
  readonly work: number;
  readonly consumes: readonly ItemAmount[];
  readonly produces: readonly ItemAmount[];
  readonly status: PieceStatus;
}

export interface BeltReport {
  readonly key: string;
  readonly cell: Cell;
  readonly exit: Side;
  /** Sides material comes in through, from the pieces feeding it in the flow network; empty when nothing feeds it. */
  readonly inputs: readonly Side[];
  readonly rate: number;
  readonly capacity: number;
  /** Items per minute it carries, by item. */
  readonly carrying: readonly ItemAmount[];
  readonly status: PieceStatus;
}

export interface ExtractionReport {
  readonly item: ItemAmount;
  /** Items per minute the terrain yields at most. */
  readonly max: number;
  readonly status: FlowStatus;
}

export interface TileReport {
  readonly id: string;
  readonly name: string;
  readonly kind: TileKind;
  readonly terrain: string;
  readonly cell: Cell;
  readonly ports: readonly PortReport[];
  readonly interior?: {
    readonly size: Cell;
    readonly machines: readonly MachineReport[];
    readonly belts: readonly BeltReport[];
  };
  readonly extraction?: ExtractionReport;
}

export interface RouteReport {
  readonly id: string;
  readonly from: { readonly tile: string; readonly name: string; readonly port: string };
  readonly to: { readonly tile: string; readonly name: string; readonly port: string };
  readonly vehicle: string;
  readonly length: number;
  readonly roundTripSeconds: number;
  /** Items per trip. */
  readonly load: number;
  /** Items per minute at most, and right now. */
  readonly max: number;
  readonly rate: number;
  readonly carrying: readonly ItemAmount[];
  readonly status: FlowStatus;
}

export interface OrderReport {
  readonly id: string;
  readonly name: string;
  readonly item: string;
  readonly delivered: number;
  readonly amount: number;
  readonly reward: number;
  readonly done: boolean;
  /** Game time when it was fulfilled. */
  readonly doneAtMs?: number;
}

export interface Report {
  readonly tick: number;
  readonly timeMs: number;
  readonly speed: Speed;
  readonly paused: boolean;
  readonly coins: number;
  readonly tiles: readonly TileReport[];
  readonly routes: readonly RouteReport[];
  readonly orders: readonly OrderReport[];
  /** What the colony warehouse holds. */
  readonly colony: readonly ItemAmount[];
  readonly undo?: string;
  readonly redo?: string;
}

const sum = (v: ReadonlyMap<ItemId, number>): number => storeTotal(v);

export function buildReport(sim: Simulation): Report {
  const { world, data } = sim;
  const figures = sim.figures;
  const network = sim.flowNetwork;
  const amounts = (v: ReadonlyMap<ItemId, number>): ItemAmount[] =>
    [...v.entries()]
      .filter(([, n]) => n > EPSILON)
      .map(([item, amount]) => {
        const def = data.items.get(item);
        return { item, name: def?.name ?? item, color: def?.color ?? '#ffffff', amount };
      });

  // The side each belt is entered from, per feeder: across the feeder's exit, or the entrance's border side.
  const beltInputs = new Map<string, Side[]>();
  const leavingSide = (node: FlowNode): Side | undefined => {
    switch (node.kind) {
      case 'belt':
        return world.tiles.get(node.tile)?.interior?.belts.get(node.key);
      case 'machine':
        return machineOutput(node.machine).side;
      default:
        return undefined;
    }
  };
  for (const line of network.lines) {
    line.nodes.forEach((node, i) => {
      if (node.kind !== 'belt') return;
      const sides: Side[] = [];
      for (const c of line.children[i] ?? []) {
        const feeder = line.nodes[c];
        if (!feeder) continue;
        if (feeder.kind === 'source') {
          const edge = world.tiles.get(feeder.port.tile)?.def.ports.find((p) => p.id === feeder.port.port)?.edge;
          if (edge) sides.push(edge.side);
          continue;
        }
        const out = leavingSide(feeder);
        if (out) sides.push(opposite(out));
      }
      beltInputs.set(`${node.tile}/${node.key}`, sides);
    });
  }

  const machines = new Map<string, NodeFigures>();
  const belts = new Map<string, NodeFigures>();
  const routes = new Map<string, NodeFigures>();
  const extraction = new Map<string, NodeFigures>();
  for (const nodes of figures.lines.values()) {
    for (const f of nodes) {
      const node = f.node;
      if (node.kind === 'machine') machines.set(node.machine.id, f);
      else if (node.kind === 'belt') belts.set(`${node.tile}/${node.key}`, f);
      else if (node.kind === 'route') routes.set(node.route.id, f);
      else if (node.kind === 'extract') extraction.set(node.tile, f);
    }
  }

  const portReport = (store: Store, port: PortDef): PortReport => {
    const flows = figures.stores.get(store);
    const total = sum(store.items);
    return {
      id: port.id,
      kind: port.kind,
      side: port.side,
      ...(port.edge ? { edge: port.edge } : {}),
      stock: amounts(store.items),
      total,
      capacity: Number.isFinite(store.capacity) ? store.capacity : null,
      full: total >= store.capacity - EPSILON * Math.max(1, store.capacity),
      inRate: flows ? sum(flows.in) : 0,
      outRate: flows ? sum(flows.out) : 0,
      arriving: amounts(flows?.in ?? new Map()),
      leaving: amounts(flows?.out ?? new Map()),
    };
  };

  const tiles = [...world.tiles.values()].map((tile): TileReport => {
    const def = tile.def;
    const ports = def.ports.flatMap((port) => {
      const store = tile.stores.get(port.id);
      return store ? [portReport(store, port)] : [];
    });
    const base = { id: def.id, name: def.name, kind: def.kind, terrain: def.terrain.name, cell: def.cell, ports };
    const extract = extraction.get(def.id);
    const resource = def.resource;
    const interior = tile.interior;
    const stranded = network.stranded.get(def.id);
    return {
      ...base,
      ...(resource && extract
        ? { extraction: { item: { item: resource.item.id, name: resource.item.name, color: resource.item.color, amount: sum(extract.out) }, max: resource.rate, status: extract.status } }
        : {}),
      ...(interior
        ? {
            interior: {
              size: interior.size,
              machines: [...interior.machines.values()].map((m): MachineReport => {
                const f = machines.get(m.id);
                const status: PieceStatus = f?.status ?? (stranded?.machines.has(m.id) ? 'disconnected' : 'starved');
                return {
                  id: m.id,
                  type: m.def.id,
                  name: m.def.name,
                  recipe: m.def.recipe.name,
                  origin: m.origin,
                  rotation: m.rotation,
                  cells: machineCells(m),
                  input: machineInput(m),
                  output: machineOutput(m),
                  crafts: f?.crafts ?? 0,
                  maxCrafts: m.def.craftsPerMinute,
                  work: (f?.crafts ?? 0) / m.def.craftsPerMinute,
                  consumes: amounts(f?.in ?? new Map()),
                  produces: amounts(f?.out ?? new Map()),
                  status,
                };
              }),
              belts: [...interior.belts.entries()].map(([key, exit]): BeltReport => {
                const f = belts.get(`${def.id}/${key}`);
                const [col = 0, row = 0] = key.split(',').map(Number);
                return {
                  key,
                  cell: [col, row],
                  exit,
                  inputs: beltInputs.get(`${def.id}/${key}`) ?? [],
                  rate: f ? sum(f.out) : 0,
                  capacity: data.belt.rate,
                  carrying: amounts(f?.out ?? new Map()),
                  status: f?.status ?? (stranded?.belts.has(key) ? 'disconnected' : 'starved'),
                };
              }),
            },
          }
        : {}),
    };
  });

  const name = (id: string): string => world.tiles.get(id)?.def.name ?? id;
  const routeReports = [...world.routes.values()].map((route): RouteReport => {
    const f = routes.get(route.id);
    return {
      id: route.id,
      from: { ...route.from, name: name(route.from.tile) },
      to: { ...route.to, name: name(route.to.tile) },
      vehicle: route.vehicle.name,
      length: route.length,
      roundTripSeconds: roundTripMinutes(route.vehicle, route.length) * 60,
      load: route.vehicle.capacity,
      max: routeThroughput(route.vehicle, route.length),
      rate: f ? sum(f.out) : 0,
      carrying: amounts(f?.out ?? new Map()),
      status: f?.status ?? 'starved',
    };
  });

  const colony = new Map<ItemId, number>();
  for (const tile of world.tiles.values()) {
    if (tile.def.kind !== 'warehouse') continue;
    for (const store of tile.stores.values()) for (const [item, n] of store.items) colony.set(item, (colony.get(item) ?? 0) + n);
  }

  const undo = sim.history.undoLabel;
  const redo = sim.history.redoLabel;
  return {
    tick: world.tick,
    timeMs: world.tick * TICK_MS,
    speed: sim.clock.speed,
    paused: sim.clock.paused,
    coins: world.coins,
    tiles,
    routes: routeReports,
    orders: world.orders.map((o) => ({
      id: o.def.id,
      name: o.def.name,
      item: o.def.item.name,
      delivered: o.delivered,
      amount: o.def.amount,
      reward: o.def.reward,
      done: o.doneAt !== undefined,
      ...(o.doneAt === undefined ? {} : { doneAtMs: o.doneAt * TICK_MS }),
    })),
    colony: amounts(colony),
    ...(undo ? { undo } : {}),
    ...(redo ? { redo } : {}),
  };
}
